import { useCallback, useEffect, useRef } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import styled from 'foundations-theming/utils';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Button } from 'foundations-components/transitional/components/Button';
import { remCalc } from 'foundations-components/transitional/utils/styles';
import UsageTracker from '../../utils/UsageTracker';
import { BaseContentControlWrapper } from '../ContentControlWrapper';
import { isInViewport } from '../../utils/utils';
import { chirpEnumToString } from 'academy-common-lib/utils/chirp';

// @bend-expect trellis-tokenized
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ChapterContentWrapper = styled.div.withConfig({
  displayName: "ChapterContent__ChapterContentWrapper"
})(({
  theme
}) => ({
  fontSize: remCalc('18px'),
  h3: {
    marginTop: remCalc(theme.space['1100'])
  },
  h4: {
    marginTop: remCalc(theme.space['600'])
  },
  img: {
    display: 'block',
    margin: `${remCalc(theme.space['500'])} auto`,
    maxWidth: '100%'
  }
}));
const ChapterContent = ({
  name,
  contents,
  contentId,
  contentLanguage,
  trackId,
  lessonId,
  completed,
  advanceToNextContent,
  onComplete,
  visibility
}) => {
  const footerRef = useRef(null);
  const completedContentIdRef = useRef(undefined);
  const maybeMarkCompleted = useCallback(() => {
    if (!completed && contents && completedContentIdRef.current !== contentId) {
      completedContentIdRef.current = contentId;
      onComplete();
    }
  }, [completed, contents, contentId, onComplete]);
  useEffect(() => {
    const checkProgress = () => {
      if (footerRef.current && isInViewport(footerRef.current)) {
        maybeMarkCompleted();
      }
    };
    document.addEventListener('scroll', checkProgress, true);
    checkProgress();
    return () => {
      document.removeEventListener('scroll', checkProgress, true);
    };
  }, [completed, contents, maybeMarkCompleted, onComplete]);
  const handleClickNext = () => {
    maybeMarkCompleted();
    advanceToNextContent === null || advanceToNextContent === void 0 || advanceToNextContent();
  };
  const handleClickOnChapterContent = event => {
    const {
      currentTarget: target
    } = Object.assign({}, event); // event bubled up to parent;

    if (target && target.href && target) {
      if (!target.href) {
        return;
      }
      if (!contentId || !trackId && !lessonId) {
        return;
      }
      UsageTracker.track('contentInteraction', {
        action: 'open-chapter-link',
        contentId,
        contentLanguage,
        contentType: 'CHAPTER',
        trackId,
        lessonId,
        resourceTitle: target.textContent,
        resourceUrl: target.href,
        visibility: chirpEnumToString(visibility)
      });
    }
  };
  const renderContent = () => {
    return (
      /*#__PURE__*/
      // @bend-expect trellis-tokenized
      _jsx(ChapterContentWrapper, {
        dangerouslySetInnerHTML: {
          __html: contents || ''
        },
        "data-test-id": "chapter-html-content",
        onClick: handleClickOnChapterContent
      })
    );
  };
  return /*#__PURE__*/_jsxs("div", {
    "data-test-id": "chapter-content",
    children: [/*#__PURE__*/_jsxs(View, {
      marginBlockStart: "1000",
      marginInline: "auto",
      maxInlineSize: "850px",
      paddingBlock: "250",
      paddingInline: "600",
      children: [/*#__PURE__*/_jsx(View, {
        borderBlockEndColor: "border-core-default",
        borderBlockEndStyle: "solid",
        borderBlockEndWidth: "100",
        marginBlockEnd: "1000",
        paddingBlockEnd: "600",
        children: /*#__PURE__*/_jsx(Text, {
          as: "h2",
          children: name
        })
      }), renderContent()]
    }), /*#__PURE__*/_jsx(BaseContentControlWrapper, {
      children: /*#__PURE__*/_jsx("div", {
        ref: ref => {
          footerRef.current = ref;
        },
        children: /*#__PURE__*/_jsx(Button, {
          onClick: handleClickNext,
          use: "primary",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.common.next"
          })
        })
      })
    })]
  });
};
export default ChapterContent;